import { useMutation } from '@apollo/client';

import GetVariableContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredVariableContractLines.query.gql';
import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import {
  ResumeContractLines,
  ResumeContractLinesVariables
} from '@/graphql/contracts/_generated_/resumeContractLines.mutation';
import ResumeContractLineMutation from '@/graphql/contracts/resumeContractLines.mutation.gql';
import { notificationService } from '@/services/notification/notification.client';
import { ALL_ITEMS } from '@/config/common.config';
import GetBCContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';
import { triggerLineRefresh } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import { triggerVariableRefresh } from 
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';

export type ResumeContractLineParams = ResumeContractLinesVariables;

export default function useResumeContractLine() {
  const [_resumeContractLine, { loading, error }] = useMutation<ResumeContractLines, ResumeContractLinesVariables>(
    ResumeContractLineMutation,
    {
      client: apolloClientContracts,
      onError: (err) => notificationService.open({
        title: 'Error',
        text: err.message,
        type: 'danger'
      }),
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        const resumedCount = data.resumeContractLines.length;
        notificationService.open({
          title: 'Resume contract lines',
          text: `${resumedCount} contract line${resumedCount > 1 ? 's have' : ' has'} been successfully resumed`,
          type: 'success'
        });
        
        triggerLineRefresh();
        triggerVariableRefresh();
      }
    }
  );

  const resumeContractLine = async (
    inputParams: ResumeContractLineParams,
    includeRegularLines: boolean = true,
    includeVariableLines: boolean = false
  ) => {
    try {
      const contractIds = new Set<string>();
      if (inputParams.contracts) {
        const contractsArray = Array.isArray(inputParams.contracts) ? inputParams.contracts : [inputParams.contracts];
        contractsArray.forEach((contract: { contractId: string }) => {
          contractIds.add(contract.contractId);
        });
      }

      // Build refetch queries array based on line type flags
      const refetchQueries: any[] = [];

      // Refetch BC lines if regular lines are included
      if (includeRegularLines) {
        refetchQueries.push({
          query: GetBCContractLinesQuery,
          variables: {
            filter: {
              contractId: { containsValues: Array.from(contractIds) }
            },
            first: ALL_ITEMS
          }
        });
      }

      // Include variable lines query if variable lines are included
      if (includeVariableLines) {
        Array.from(contractIds).forEach(contractId => {
          refetchQueries.push({
            query: GetVariableContractLinesQuery,
            variables: {
              filter: {
                headerPrimaryKey: { eq: contractId }
              },
              first: ALL_ITEMS
            }
          });
        });
      }

      const result = await _resumeContractLine({
        variables: inputParams,
        refetchQueries
      });

      if (result.data?.resumeContractLines) {
        return true;
      } else {
        return false;
      }
    } catch (resumeError) {
      return false;
    }
  };

  return {
    resumeContractLine,
    isLoadingResumeContractLine: loading,
    errorResumeContractLine: error,
  } as const;
}
